import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { SKU, SKUFormData, TERM_OPTIONS, CURRENCY_OPTIONS, TermType } from '../../types/sku';

interface SKUModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: SKUFormData) => Promise<void>;
  editSku?: SKU | null;
  existingSkuNames?: string[];
}

interface FormErrors {
  sku?: string;
  price?: string;
  term?: string;
  description?: string;
}

export const SKUModal: React.FC<SKUModalProps> = ({
  open,
  onClose,
  onSubmit,
  editSku,
  existingSkuNames = [],
}) => {
  const isEditMode = !!editSku;

  const [formData, setFormData] = useState<SKUFormData>({
    sku: '',
    price: 0,
    currency: 'USD',
    term: 'weekly',
    termSku: '',
    isActive: true,
    description: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (editSku) {
        setFormData({
          sku: editSku.sku,
          price: editSku.price,
          currency: editSku.currency || 'USD',
          term: editSku.term,
          termSku: editSku.termSku || '',
          isActive: editSku.isActive,
          description: editSku.description || '',
        });
      } else {
        setFormData({
          sku: '',
          price: 0,
          currency: 'USD',
          term: 'weekly',
          termSku: '',
          isActive: true,
          description: '',
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [open, editSku]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU identifier is required';
    } else if (formData.sku.includes(' ')) {
      newErrors.sku = 'SKU cannot contain spaces';
    } else if (formData.sku.length > 100) {
      newErrors.sku = 'SKU must be less than 100 characters';
    } else if (!isEditMode && existingSkuNames.includes(formData.sku)) {
      newErrors.sku = 'This SKU already exists';
    }

    if (formData.price < 0) {
      newErrors.price = 'Price must be a positive number';
    }

    if (!formData.term) {
      newErrors.term = 'Term type is required';
    }

    if (formData.description && formData.description.length > 200) {
      newErrors.description = 'Description must be less than 200 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setSubmitError(null);

    try {
      await onSubmit(formData);
      onClose();
    } catch (error: any) {
      setSubmitError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof SKUFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {isEditMode ? 'Edit SKU' : 'Add New SKU'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="SKU Identifier"
            value={formData.sku}
            onChange={(e) => handleChange('sku', e.target.value)}
            error={!!errors.sku}
            helperText={errors.sku || 'The product SKU from Amazon Appstore'}
            disabled={isEditMode}
            required
            fullWidth
            size="small"
            placeholder="weekly_premium_subscriptions_699"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Price"
              type="number"
              value={formData.price}
              onChange={(e) => handleChange('price', parseFloat(e.target.value) || 0)}
              error={!!errors.price}
              helperText={errors.price}
              required
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ flex: 2 }}
              size="small"
              placeholder="6.99"
            />

            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                label="Currency"
              >
                {CURRENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    {opt.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth size="small" error={!!errors.term} required>
            <InputLabel>Term Type</InputLabel>
            <Select
              value={formData.term}
              onChange={(e) => handleChange('term', e.target.value as TermType)}
              label="Term Type"
            >
              {TERM_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {errors.term && <FormHelperText>{errors.term}</FormHelperText>}
          </FormControl>

          <TextField
            label="Term SKU (Optional)"
            value={formData.termSku}
            onChange={(e) => handleChange('termSku', e.target.value)}
            helperText="Only if different from main SKU"
            fullWidth
            size="small"
            placeholder="weekly_premium_subscriptions_699_term"
          />

          <TextField
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description || `${formData.description?.length || 0}/200 characters`}
            multiline
            rows={2}
            fullWidth
            size="small"
            placeholder="Premium weekly subscription at $6.99"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" color={formData.isActive ? 'success.main' : 'text.secondary'}>
                {formData.isActive ? 'Active' : 'Inactive'}
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : isEditMode ? (
            'Save'
          ) : (
            'Add SKU'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
